import React, { useState, useEffect } from "react";
import { getExercises, createOperation } from "../../api";
import MatrixGrid from "../matrixGrid/MatrixGrid";
import styles from "./AddExercisesBox.module.css";
import { determinant, EigenvalueDecomposition, Matrix } from "ml-matrix";

import { OPERATION_TYPE } from "../../constants";

function roundToThree(num) {
    return (Math.round((num + Number.EPSILON) * 1000) / 1000)
}

const AddExercisesBox = () => {
    const [operation, setOperation] = useState(OPERATION_TYPE.DETERMINANT);
    const [response, setResponse] = useState('');

    const columns = 3;
    const rows = 3;

    const [matrix1, setMatrix1] = useState(new Matrix(rows, columns));
    const [matrix2, setMatrix2] = useState(new Matrix(rows, columns));

    useEffect(() => {
        setResponse('');
        setMatrix1(new Matrix(rows, columns));
        setMatrix2(new Matrix(rows, columns));
    }, [operation]);

    const handleSubmit = (e, id) => {
        e.preventDefault();

        const data = {
            'operationType': operation.getCode(),
            "userId": "asdgfs",
            'matrix1': {
                'columns': matrix1.columns,
                'rows': matrix1.rows,
                'values': matrix1.to1DArray()
            },
            'matrix2': {
                'columns': matrix2.columns,
                'rows': matrix2.rows,
                'values': matrix2.to1DArray()
            }
        };

        if (operation === OPERATION_TYPE.ADDITION) {
            data['result_matrix'] = {
                'columns': matrix1.columns,
                'rows': matrix1.rows,
                'values': Matrix.add(matrix1, matrix2).to1DArray()
            }
        } else if (operation === OPERATION_TYPE.SUBTRACTION) {
            data['result_matrix'] = {
                'columns': matrix1.columns,
                'rows': matrix1.rows,
                'values': Matrix.sub(matrix1, matrix2).to1DArray()
            }
        } else if (operation === OPERATION_TYPE.MULTIPLICATION) {
            data['result_matrix'] = {
                'columns': matrix1.columns,
                'rows': matrix1.rows,
                'values': Matrix.mul(matrix1, matrix2).to1DArray()
            }
        } else if (operation === OPERATION_TYPE.DETERMINANT) {
            data['determinant'] = roundToThree(determinant(matrix1));
        } else if (operation === OPERATION_TYPE.EIGENVALUES) {
            data['eigenvalues'] = (new EigenvalueDecomposition(matrix1)).realEigenvalues;
        } else if (operation === OPERATION_TYPE.EIGENVECTORS) {
            data['eigenvectors'] = (new EigenvalueDecomposition(matrix1)).eigenvectorMatrix.to1DArray()
        }

        createOperation(data)
            .then(res => setResponse('Saved'))
            .catch(err => setResponse('Something went wrong'))
    }

    return (
        <div className={styles.box_wrapper}>
            <p className={styles.operation_text}>
                <mark className={styles.highlight}>Select the matrix property you want to operate with:
                    <select className={styles.selection_box} value={operation.getCode()} onChange={e => setOperation(OPERATION_TYPE.fromCode(e.target.value))}>
                        <option value={1}>Addition</option>
                        <option value={2}>Subtraction</option>
                        <option value={3}>Multiplication</option>
                        <option value={4}>Determinant</option>
                        <option value={5}>Eigenvalues</option>
                        <option value={6}>Eigenvector</option>
                    </select>
                </mark></p>
            <p className={styles.operation_text}>Calculate the <b>{operation.toString()}</b> of the matrix shown below:</p>
            <div className={styles.matrix}>
                <MatrixGrid matrix={matrix1} setMatrix={setMatrix1} editable={true} index={1} />
                {operation.getCode() <= 3 &&
                    <p>{operation === OPERATION_TYPE.ADDITION ?
                        '+' :
                        operation === OPERATION_TYPE.ADDITION ?
                            '-' :
                            'x'}
                    </p>
                }
                {operation.getCode() <= 3 && <MatrixGrid matrix={matrix2} setMatrix={setMatrix2} editable={true} index={2} />}
                {operation.getCode() <= 3 && <p>=</p>}

                <p className={styles.result_text}>
                    {operation === OPERATION_TYPE.ADDITION &&
                        <p>
                            Answer:<br />
                            <MatrixGrid matrix={Matrix.add(matrix1, matrix2)} editable={false} />
                        </p>
                    }
                    {operation === OPERATION_TYPE.SUBTRACTION &&
                        <p>
                            Answer:<br />
                            <MatrixGrid matrix={Matrix.sub(matrix1, matrix2)} editable={false} />
                        </p>
                    }
                    {operation === OPERATION_TYPE.MULTIPLICATION &&
                        <p>
                            Answer:<br />
                            <MatrixGrid matrix={Matrix.mul(matrix1, matrix2)} editable={false} />
                        </p>
                    }
                    {operation === OPERATION_TYPE.DETERMINANT &&
                        <p>
                            The determinant is: <b>{roundToThree(determinant(matrix1))}</b>
                        </p>
                    }
                    {operation === OPERATION_TYPE.EIGENVALUES &&
                        <p>The eigenvalues are:
                            {(new EigenvalueDecomposition(matrix1)).realEigenvalues.map((eigenvalue, index) =>
                                <p>λ<sub>{index}</sub> : <b>{roundToThree(eigenvalue)}</b></p>)}
                        </p>
                    }
                    {operation === OPERATION_TYPE.EIGENVECTORS &&
                        <p>The eigenvectors are:
                            {(new EigenvalueDecomposition(matrix1)).eigenvectorMatrix.data.map(row =>
                                <p>
                                    <span className={styles.eigenvector_styling}>
                                        {roundToThree(row[0])}
                                    </span> <span className={styles.eigenvector_styling2}>
                                        {roundToThree(row[1])}
                                    </span> <span className={styles.eigenvector_styling}>
                                        {roundToThree(row[2])}
                                    </span>
                                </p>)}
                        </p>
                    }
                    <button onClick={handleSubmit}>Create</button>
                    <p>{response}</p>
                </p>
            </div>
        </div>
    );
}

export default AddExercisesBox;